/**
 * A phone photo, made small enough to travel to Lumi (ADR 0106, the 10 MB
 * upload cap).
 *
 * A phone camera's own JPEG runs from 3 to well over 10 MB, and the large ones
 * came back from the composer as "larger than 10 MB" — the timesheet taken with
 * the camera button among them. A JPEG above SHRINK_ABOVE_BYTES is redrawn at
 * most MAX_EDGE pixels on its long side, which keeps handwriting legible for the
 * reading step (`suite_read_attachment`) and lands at a megabyte or two. Every
 * other file — a PDF, a DOCX, a PNG (a screenshot, whose transparency a JPEG
 * cannot keep), a small photo — goes as it is, and so does a photo in a browser
 * without the canvas APIs or whose redraw would not be smaller.
 */
export const SHRINK_ABOVE_BYTES = 4 * 1024 * 1024;
const MAX_EDGE = 2560;
const QUALITY = 0.85;

/** Whether `shrinkPhoto` would try this file at all. */
export function canShrinkPhoto(file: File): boolean {
  return (
    file.type === 'image/jpeg' &&
    file.size > SHRINK_ABOVE_BYTES &&
    typeof createImageBitmap === 'function' &&
    typeof document !== 'undefined'
  );
}

export async function shrinkPhoto(file: File): Promise<File> {
  if (!canShrinkPhoto(file)) return file;
  try {
    // `from-image`: the camera's EXIF rotation is applied, so the redraw stands
    // the way the photo was taken.
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name, { type: 'image/jpeg', lastModified: file.lastModified });
  } catch {
    return file;
  }
}
